import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useCaps } from '../../../contexts/CapabilitiesContext';
import { fieldClass } from '../styles';
import { fetchAdminCreditPricingTiers, patchAdminCreditPricingBase } from './api';
import {
  CURRENCY_CODES,
  CURRENCY_CONVERSION_CAP,
  type CreditPricingTierRow,
  type CurrencyCode,
} from './types';

function formatCreditRange(min: number, max: number): string {
  return `${min.toLocaleString('en-US')} – ${max.toLocaleString('en-US')}`;
}

function formatDiscount(value: string): string {
  return `${Number(value).toFixed(2)}%`;
}

function formatCostPerCredit(value: string): string {
  return Number(value).toFixed(1);
}

function costFromBase(base: number, discountPercent: string): string {
  const discount = Number(discountPercent);
  const factor = (100 - (Number.isFinite(discount) ? discount : 0)) / 100;
  return (Math.round(base * factor * 10) / 10).toFixed(1);
}

function parsedBase(raw: string | undefined): number | null {
  if (raw == null || raw.trim() === '') return null;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) return null;
  return value;
}

const AdminCurrencyConversionTab: React.FC = () => {
  const { can } = useCaps();
  const [tiers, setTiers] = useState<CreditPricingTierRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAdminCreditPricingTiers();
        if (!cancelled) setTiers(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load credit pricing tiers');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const byCurrency = useMemo(() => {
    const grouped: Record<string, CreditPricingTierRow[]> = {};
    for (const code of CURRENCY_CODES) grouped[code] = [];
    for (const tier of tiers) {
      const key = tier.currency_code;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(tier);
    }
    for (const rows of Object.values(grouped)) {
      rows.sort(
        (a, b) => a.display_order - b.display_order || a.min_credits - b.min_credits
      );
    }
    return grouped;
  }, [tiers]);

  const currencyKeys = useMemo(() => {
    const extras = Object.keys(byCurrency).filter(
      (code) => !CURRENCY_CODES.includes(code as CurrencyCode)
    );
    return [...CURRENCY_CODES, ...extras];
  }, [byCurrency]);

  const displayedCost = (code: string, row: CreditPricingTierRow, isFirst: boolean) => {
    if (isFirst) return drafts[code] ?? formatCostPerCredit(row.cost_per_credit);
    const base = parsedBase(drafts[code]);
    if (base == null) return formatCostPerCredit(row.cost_per_credit);
    return costFromBase(base, row.discount_percent);
  };

  const saveBase = async (code: string, first: CreditPricingTierRow | undefined, raw: string) => {
    if (!first) return;
    const base = parsedBase(raw);
    if (base == null) {
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      return;
    }
    const normalized = base.toFixed(1);
    if (normalized === formatCostPerCredit(first.cost_per_credit)) {
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      return;
    }
    setSaving(code);
    setError(null);
    try {
      const updated = await patchAdminCreditPricingBase(code, normalized);
      setTiers((prev) => [
        ...prev.filter((row) => row.currency_code !== code),
        ...updated,
      ]);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update cost per credit');
    } finally {
      setSaving(null);
    }
  };

  if (!can(CURRENCY_CONVERSION_CAP)) {
    return (
      <div className="text-sm text-[#8A8A8A]">
        You do not have permission to view currency conversion.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-[#626262]">
        Edit the first cost per credit for a currency. The other rows update from that base using each row’s discount.
      </p>
      {error && <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {currencyKeys.map((code) => {
          const rows = byCurrency[code] || [];
          const first = rows[0];
          return (
            <div key={code}>
              <div className="mb-2 flex items-center gap-2">
                <h2 className="text-[15px] font-semibold text-[#0C0B0B]">{code}</h2>
                {saving === code && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#8A8A8A]" />
                )}
              </div>
              <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-[#FAFAFA] text-[12px] text-[#626262]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Credits</th>
                      <th className="text-left px-3 py-2 font-medium">Discount</th>
                      <th className="text-right px-3 py-2 font-medium">Cost per credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const isFirst = index === 0;
                      return (
                        <tr key={row.id} className="border-t border-[#F0F0F0]">
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatCreditRange(row.min_credits, row.max_credits)}
                          </td>
                          <td className="px-3 py-2">{formatDiscount(row.discount_percent)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {isFirst ? (
                              <input
                                type="number"
                                min="0"
                                step="0.1"
                                inputMode="decimal"
                                disabled={saving === code}
                                value={displayedCost(code, row, true)}
                                onChange={(event) =>
                                  setDrafts((prev) => ({ ...prev, [code]: event.target.value }))
                                }
                                onBlur={(event) => void saveBase(code, first, event.target.value)}
                                className={`${fieldClass} ml-auto w-[96px] px-2 py-1 text-right`}
                              />
                            ) : (
                              displayedCost(code, row, false)
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-3 py-8 text-center text-[#8A8A8A]">
                          No pricing tiers for {code}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminCurrencyConversionTab;
